import React, { useEffect, useState } from 'react'
import axios from 'axios'

import { graphql } from 'gatsby'
import Hero from '../components/Hero/Hero'
import ButtonVariantEnum from '../enums/ButtonVariant'
import SpecificContent from '../components/SpecificContent/SpecificContent'
import SideNavigation, { SideNavTypeEnum } from '../components/SideNavigation/SideNavigation'
import useScreenOrientation from '../hooks/useScreenOrientation'
import Seo from '../components/Seo/Seo'
import {
  StyledContactButton,
  StyledContentData,
  StyledDescriptionText,
  StyledDocumentsCarousel,
  StyledHeaderContainer,
  StyledHighlight,
  StyledHighlightContainer,
  StyledLayout,
  StyledLeftSide,
  StyledMainContainer,
  StyledMainLayout,
  StyledProductButton,
  StyledRecommendedBody,
  StyledRecommendedContent,
  StyledRecommendedHeader,
  StyledRightSide,
  StyledSearchBarProducts,
  StyledSpecific,
} from './product.styled'
import Carousel, { CarouselVariantEnum } from '../components/Carousel/Carousel'
import SlideMedia from '../components/Carousel/SlideMedia/SlideMedia'
import SlideDocument from '../components/Carousel/SlideDocument/SlideDocument'
import CKEditorContent from '../components/CKEditorContent/CKEditorContent'
import LocalesTab, { templateEnum } from '../components/LocalesTab/LocalesTab'
import RoutingEnum from '../routes/appRoutes'
import MarketsEnum from '../enums/MarketsEnum'
import { productsSearch, suggest } from '../routes/apiRoutes'
import NotFoundPage from '../pages/404'
import contactSubjects from '../data/contactSubjects'
import SectorWithGeo from '../data/sectorWithGeo'
import { GetFirstSentenceFromRichText } from '../utils/GetFirstSentenceFromRichText'
import useMediaQuery from '../hooks/useMediaQuery'
import { deviceMax } from '../theme/breakpoints'
import { APP_AERO_URL, APP_AUTO_URL } from '../config/AppAutoAero'
import NormalizeMedia from '../utils/NormalizeMedia'
import { StyledVideo } from './story.styled'
import { API_APP_KEY } from '../config/ApiConfig'

const CONTACT_LABEL = 'Contact Product Team'
const PORTRAIT_PRIMARY = 0

const Product = ({ data, location, pageContext }) => {
  if (data?.contentfulTemplateProduct === null) {
    return <NotFoundPage location={location} />
  }

  const {
    productLocales,
    backgroundColor,
    brandBlocs,
    carrousselMedias,
    carrousselTitle,
    descriptionHtml,
    productButton,
    title,
    recommendedContentTitle,
    recommendedContentItems,
    imageHeaderDesktop,
    imageHeaderMobile,
    documentsList,
    documentsBlocTitle,
    displayProductButton,
    documentsBlocDisplay,
    seoMetaDescription,
    sector,
    application,
    video
  } = data.contentfulTemplateProduct

  const getMarkets = (markets) => {
    return markets.reduce((acc, currentMarket) => {
      acc.push({ label: currentMarket, value: currentMarket })
      return acc
    }, [])
  }

  const metaTitle = title + '-' + sector

  const isMobile = useMediaQuery(deviceMax.mobileL)
  const isTablet = useMediaQuery(deviceMax.tablet)
  const screenOrientation = useScreenOrientation()

  const initialFilters = {
    markets: [],
  }

  const initialOptions = {
    markets: getMarkets(MarketsEnum) || [],
  }

  const [suggestions, setSuggestions] = useState([])
  const [keywords, setKeywords] = useState('')
  const [filters, setFilters] = useState(initialFilters)
  const [options, setOptions] = useState(initialOptions)
  const [productsList, setProductsList] = useState([])

  const [slidesDocument, setSlidesDocument] = useState(null)
  const [slidesMedia, setSlidesMedia] = useState(null)
  const documents = SlideDocument(documentsList, documentsBlocDisplay)
  const medias = SlideMedia(carrousselMedias)
  const [metaDesc, setMetaDesc] = useState('')

  useEffect(() => {
    const metadesc = GetFirstSentenceFromRichText(descriptionHtml?.content)
    setMetaDesc(metadesc)
  }, [])

  useEffect(() => {
    setSlidesDocument(documents)
    setSlidesMedia(medias)
  }, [])

  const scrollTop = () => {
    // scroll top
    const scrollTop = document
      .querySelector('.requestScrollTo')
      .getBoundingClientRect().top

    if (window) {
      window?.scrollBy({
        top: scrollTop,
        left: 0,
        behavior: 'smooth',
      })
    }
  }

  const getParams = () => {
    const m = filters.markets.join('_')
    const q = keywords

    return { m, q }
  }

  const handleAutocompletion = async (newKeywords) => {
    if (!newKeywords) return setSuggestions([])
    try {
      const result = await axios.get(`${suggest}/products?code=${API_APP_KEY}`, {
        headers: {
          'Cache-Control': 'no-cache',
        },
        params: { q: newKeywords },
      })
      setSuggestions(result?.data)
    } catch (error) {
      console.error(error)
    }
  }

  const handleNewSearch = async () => {
    if (!filters.markets && keywords) return

    const params = getParams()
    const result = await axios.get(productsSearch, {
      headers: {
        'Cache-Control': 'no-cache',
      },
      params,
    })

    const newProducts = result.data.hits
    setProductsList(newProducts)

    // scroll top
    scrollTop()
  }

  const showSideNavigation = !(
    isMobile ||
    (isTablet && screenOrientation === PORTRAIT_PRIMARY)
  )

  const stateBtnContact = {
    subject: contactSubjects.find((e) => e.label === 'Customer request'),
    sector: SectorWithGeo.find((e) => e.label === sector) ?? null,
    applications:
      SectorWithGeo?.find((e) => e.label === sector)?.application ?? [],
    application:
      SectorWithGeo?.find((e) => e.label === sector)?.application?.find(
        (e) => e.label === application
      ) ?? null,
    geographies:
      SectorWithGeo?.find((e) => e.label === sector)?.application?.find(
        (e) => e.label === application
      )?.geography ?? [],
  }

  const specificContents = brandBlocs
    ? brandBlocs.map((brand, index) => (
        <StyledSpecific key={brand.id + index}>
          <SpecificContent
            key={brand.id}
            title={brand.title}
            text={brand.content}
            textHtml={brand.contentTextHtml}
          />
        </StyledSpecific>
      ))
    : null
  const recommended = recommendedContentItems
    ? recommendedContentItems.map((item, index) => {
        const hightLightUrl = item.cta?.urlAppAuto
          ? APP_AUTO_URL
          : item.cta?.urlAppAero
          ? APP_AERO_URL
          : item.cta?.url
        return (
          <StyledHighlightContainer key={index}>
            <StyledHighlight
              key={item.id + index}
              ctaCardLabel={item.cta?.label}
              document={item.document}
              image={item.image}
              textHtml={item.textHtml}
              textAlignment={item.textAlignment}
              title={item.title?.title}
              url={hightLightUrl}
              buttonVariant={item.buttonVariant}
            />
          </StyledHighlightContainer>
        )
      })
    : null

  return (
    <StyledLayout $isLight={false}>
      <Seo
        title={metaTitle}
        description={
          seoMetaDescription ? seoMetaDescription.seoMetaDescription : metaDesc
        }
        canonicalHref={location.pathname}
      />
      <Hero
        mediaDesktop={imageHeaderDesktop}
        mediaMobile={imageHeaderMobile}
        title={title}
        backgroundColor={backgroundColor}
        pathname={location.pathname}
      />
      <StyledSearchBarProducts
        handleAutocompletion={handleAutocompletion}
        handleNewSearch={handleNewSearch}
        keywords={keywords}
        filters={filters}
        options={options}
        setFilters={setFilters}
        setKeywords={setKeywords}
        suggestions={suggestions}
        productsList={productsList}
      />
      <StyledHeaderContainer $hideComponent={true}>
        <StyledContactButton
          label={CONTACT_LABEL}
          $hideComponent={true}
          variant={ButtonVariantEnum.primary}
          to={RoutingEnum.contact}
          state={stateBtnContact}
        />
      </StyledHeaderContainer>
      <StyledMainLayout $isLight={false}>
        <StyledLeftSide>
          {productLocales && (
            <StyledMainContainer>
              <LocalesTab
                locales={productLocales}
                slugs={pageContext.productSlugs}
                template={templateEnum.product}
                pageCategory="product"
              />
            </StyledMainContainer>
          )}
          <StyledContactButton
            label={CONTACT_LABEL}
            variant={ButtonVariantEnum.primary}
            $hideComponent={false}
            to={RoutingEnum.contact}
            state={stateBtnContact}
          />
          {showSideNavigation && (
            <SideNavigation variant={SideNavTypeEnum.product} />
          )}
          {isMobile && displayProductButton && (
            <StyledProductButton
              label={productButton.label}
              variant={ButtonVariantEnum.secondary}
              to={productButton.slug}
              href={productButton.url}
            />
          )}
        </StyledLeftSide>
        <StyledRightSide>
          {!isMobile && displayProductButton && (
            <StyledProductButton
              label={productButton.label}
              variant={ButtonVariantEnum.secondary}
              to={productButton.slug}
              href={productButton.url}
            />
          )}
          {descriptionHtml?.content && (
            <StyledDescriptionText>
              <CKEditorContent html={descriptionHtml.content} />
            </StyledDescriptionText>
          )}
          {video && <StyledVideo>{NormalizeMedia(video)}</StyledVideo>}
          {specificContents && specificContents}
          <StyledContentData>
            {slidesMedia && (
              <Carousel
                title={carrousselTitle ? carrousselTitle : undefined}
                variant={CarouselVariantEnum.media}
              >
                {slidesMedia}
              </Carousel>
            )}
            {slidesDocument && (
              <StyledDocumentsCarousel
                title={documentsBlocTitle ? documentsBlocTitle : undefined}
                variant={CarouselVariantEnum.document}
                orientation={documentsBlocDisplay}
              >
                {slidesDocument}
              </StyledDocumentsCarousel>
            )}
          </StyledContentData>
        </StyledRightSide>
      </StyledMainLayout>
      <StyledRecommendedContent>
        {recommended && recommendedContentTitle != null && (
          <StyledRecommendedHeader>
            {recommendedContentTitle}
          </StyledRecommendedHeader>
        )}
        {recommended && (
          <StyledRecommendedBody>{recommended}</StyledRecommendedBody>
        )}
      </StyledRecommendedContent>
    </StyledLayout>
  )
}

export default Product

export const query = graphql`
  query ProductQuery($contentful_id: String!) {
    contentfulTemplateProduct(contentful_id: { eq: $contentful_id }) {
      sector
      application
      productLocales
      title
      seoMetaDescription {
        seoMetaDescription
      }
      id
      createdAt
      backgroundColor
      displayProductButton
      imageHeaderDesktop {
          ...imageHeaderFragment
      }
      imageHeaderMobile {
          ...imageHeaderFragment
      }
      descriptionHtml {
        content
      }
      video {
          ...mediaFragment
      }
      productButton {
        url
        label
      }
      brandBlocs {
        id
        title {
          title
        }
        content {
          raw
        }
        contentTextHtml {
          content
        }
      }
      carrousselMedias {
          ...mediaFragment
      }
      carrousselTitle
      recommendedContentTitle
      recommendedContentItems {
        buttonVariant
        title {
          title
        }
        cta {
          label
          url
          urlAppAuto
          urlAppAero
        }
        document {
            ...documentFragment
        }
        image {
            ...mediaFragment
        }
        textHtml {
          content
        }
        textAlignment
        id
      }
      documentsBlocDisplay
      documentsBlocTitle
      documentsList {
          ...documentFragment
      }
    }
  }
  
`
